import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Client } from '../types/api.types';
import { login as apiLogin, register as apiRegister, getCurrentClient, logout as apiLogout } from '../services/auth.service';
import { getToken, clearToken, isAuthError, redirectToLogin } from '../utils';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

/**
 * Główny typ kontekstu uwierzytelnienia
 * Zawiera dane użytkownika oraz funkcje do zarządzania sesją
 */
interface AuthContextType {
  /** Dane zalogowanego użytkownika lub null jeśli wylogowany */
  client: Client | null;
  /** Czy użytkownik jest zalogowany */
  isAuthenticated: boolean;
  /** Czy trwa ładowanie danych użytkownika */
  isLoading: boolean;
  /** Komunikat błędu lub null */
  error: string | null;
  /** Funkcja logowania użytkownika */
  login: (email: string, password: string) => Promise<void>;
  /** Funkcja rejestracji nowego użytkownika */
  register: (registerData: RegisterData) => Promise<void>;
  /** Funkcja wylogowania użytkownika */
  logout: () => Promise<void>;
  /** Funkcja odświeżania danych użytkownika */
  refreshClient: () => Promise<void>;
}

/**
 * Dane wymagane do rejestracji nowego użytkownika
 */
export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  street: string;
  buildingNumber: string;
  apartmentNumber?: string;
  postalCode: string;
  city: string;
  phone: string;
  accountNumber: string;
  termsConsent: boolean;
  dataProcessingConsent: boolean;
  marketingConsent: boolean;
  phoneMarketingConsent: boolean;
  isCompany: boolean;
  nip?: string;
  companyName?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const STORAGE_TOKEN_KEY = 'auth_token';

const ERROR_MESSAGES = {
  SESSION_EXPIRED: 'Sesja wygasła. Zaloguj się ponownie.',
  NETWORK_ERROR: 'Błąd połączenia z serwerem.',
  LOGIN_ERROR: 'Błąd logowania. Sprawdź swoje dane.',
  REGISTRATION_ERROR: 'Błąd rejestracji. Spróbuj ponownie.',
} as const;

// ============================================================================
// CONTEXT
// ============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

/**
 * AuthProvider - komponent kontekstu zarządzający uwierzytelnieniem
 * 
 * Odpowiedzialności:
 * - Zarządzanie stanem zalogowanego użytkownika
 * - Synchronizacja stanu między kartami przeglądarki
 * - Obsługa błędów uwierzytelnienia
 * - Anulowanie requestów przy odmontowaniu
 * 
 * @example
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  // --------------------------------------------------------------------------
  // STATE
  // --------------------------------------------------------------------------
  
  const [client, setClient] = useState<Client | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Ref do śledzenia czy jest aktywny request
  const loadingRef = React.useRef<AbortController | null>(null);

  // --------------------------------------------------------------------------
  // HELPERS
  // --------------------------------------------------------------------------

  /**
   * Resetuje stan użytkownika do wylogowanego
   */
  const resetClientState = useCallback(() => {
    setClient(null);
    setIsLoading(false);
    setError(null);
  }, []);

  /**
   * Obsługuje błędy uwierzytelnienia
   * - Błąd auth → wylogowanie + przekierowanie
   * - Błąd sieci → zachowanie stanu użytkownika
   */
  const handleLoadError = useCallback((error: unknown) => {
    console.error('Failed to load client:', error);
    
    if (isAuthError(error)) {
      clearToken();
      setClient(null);
      setError(ERROR_MESSAGES.SESSION_EXPIRED);
      
      // Przekieruj do logowania (używa window.location.href)
      redirectToLogin();
    } else {
      // Błąd sieci - nie czyścimy stanu użytkownika
      setError(ERROR_MESSAGES.NETWORK_ERROR);
    }
  }, []);

  // --------------------------------------------------------------------------
  // MAIN FUNCTIONS
  // --------------------------------------------------------------------------

  /**
   * Ładuje dane użytkownika z serwera
   * 
   * @param signal - AbortSignal do anulowania requestu
   */
  const loadClient = useCallback(async (signal?: AbortSignal) => {
    const token = getToken();
    
    // Brak tokenu = wylogowany użytkownik
    if (!token) {
      resetClientState();
      return;
    }

    // Anuluj poprzedni request jeśli istnieje
    if (loadingRef.current && !signal) {
      loadingRef.current.abort();
    }

    // Utwórz nowy controller jeśli nie przekazano signalu
    const controller = signal ? null : new AbortController();
    const requestSignal = signal || controller?.signal;
    
    if (controller) {
      loadingRef.current = controller;
    }

    try {
      const clientData = await getCurrentClient(requestSignal);
      
      // Sprawdź czy request nie został anulowany
      if (requestSignal?.aborted) return;
      
      setClient(clientData);
      setError(null);
    } catch (error) {
      // Sprawdź czy request nie został anulowany
      if (requestSignal?.aborted) return;
      
      // Ignoruj błędy AbortError
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      
      handleLoadError(error);
    } finally {
      // Ustaw loading na false tylko jeśli nie anulowano
      if (!requestSignal?.aborted) {
        setIsLoading(false);
        if (controller) {
          loadingRef.current = null;
        }
      }
    }
  }, [resetClientState, handleLoadError]);

  /**
   * Loguje użytkownika
   */
  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    
    // Anuluj poprzedni request ładowania jeśli istnieje
    if (loadingRef.current) {
      loadingRef.current.abort();
    }
    
    try {
      await apiLogin({ email, password });
      await loadClient();
    } catch (error) {
      console.error('Login error:', error);
      setError(ERROR_MESSAGES.LOGIN_ERROR);
      throw error;
    }
  }, [loadClient]);

  /**
   * Rejestruje nowego użytkownika. Backend wysyła kod aktywacji na email.
   * Po rejestracji klient jest przekierowywany na stronę logowania (bez auto-logowania).
   */
  const register = useCallback(async (registerData: RegisterData) => {
    setError(null);
    
    if (loadingRef.current) {
      loadingRef.current.abort();
    }
    
    try {
      await apiRegister(registerData);
      // Brak tokenu w odpowiedzi – nie wywołujemy loadClient
    } catch (error) {
      console.error('Registration error:', error);
      setError(ERROR_MESSAGES.REGISTRATION_ERROR);
      throw error;
    }
  }, []);

  /**
   * Wylogowuje użytkownika
   */
  const logout = useCallback(async () => {
    setError(null);
    
    // Anuluj aktywne requesty
    if (loadingRef.current) {
      loadingRef.current.abort();
      loadingRef.current = null;
    }
    
    try {
      await apiLogout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearToken();
      setClient(null);
    }
  }, []);

  /**
   * Odświeża dane użytkownika z serwera
   */
  const refreshClient = useCallback(async () => {
    setError(null);
    await loadClient();
  }, [loadClient]);

  // --------------------------------------------------------------------------
  // EFFECTS
  // --------------------------------------------------------------------------

  /**
   * Effect: Inicjalizacja i synchronizacja między kartami
   * 
   * - Ładuje dane użytkownika przy montowaniu
   * - Nasłuchuje zmian w localStorage (inne karty)
   * - Anuluje requesty przy odmontowaniu
   */
  useEffect(() => {
    const abortController = new AbortController();
    
    // Załaduj dane użytkownika
    loadClient(abortController.signal);

    /**
     * Obsługuje zmiany tokenu w innych kartach
     * localStorage event uruchamia się TYLKO w innych kartach
     */
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== STORAGE_TOKEN_KEY) return;
      
      if (event.newValue) {
        // Token dodany/zmieniony w innej karcie → zaloguj tutaj
        loadClient();
      } else {
        // Token usunięty w innej karcie → wyloguj tutaj
        setClient(null);
        setError(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Cleanup: anuluj requesty i usuń listener
    return () => {
      abortController.abort();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [loadClient]);

  // --------------------------------------------------------------------------
  // CONTEXT VALUE
  // --------------------------------------------------------------------------

  const value: AuthContextType = {
    client,
    isAuthenticated: !!client,
    isLoading,
    error,
    login,
    register,
    logout,
    refreshClient,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================================================
// CUSTOM HOOK
// ============================================================================

/**
 * Hook do dostępu do kontekstu uwierzytelnienia
 * 
 * @throws {Error} Jeśli użyty poza AuthProvider
 * 
 * @example
 * function MyComponent() {
 *   const { client, isAuthenticated, login, logout } = useAuth();
 *   
 *   if (!isAuthenticated) {
 *     return <LoginForm onSubmit={login} />;
 *   }
 *   
 *   return <div>Witaj, {client?.firstName}!</div>;
 * }
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}
